import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {object} Plan
 * @property {number} id
 * @property {string} name
 * @property {number} priceCents
 * @property {number} compareCents
 */

/**
 * @typedef {object} Flavour
 * @property {string} handle
 * @property {string} title
 * @property {string} label
 * @property {string} emoji
 * @property {number} variantId
 * @property {boolean} available
 * @property {number} servings
 * @property {number} oneTimeCents
 * @property {string} image
 * @property {Record<string, Plan>} plans
 */

/**
 * @typedef {object} Config
 * @property {string} moneyFormat
 * @property {string} [currency]
 * @property {number} cafeServeCents
 * @property {number} defaultServings
 * @property {string} currentHandle
 * @property {string} productTitle
 * @property {Flavour[]} flavours
 * @property {{id: number, alt: string, src: string, thumb: string}[]} media
 */

/**
 * @typedef {object} Refs
 * @property {HTMLScriptElement} config
 * @property {HTMLElement} stage
 * @property {HTMLImageElement} stageImage
 * @property {HTMLElement} mixPreview
 * @property {HTMLElement} thumbs
 * @property {HTMLElement} supplies
 * @property {HTMLElement} mixHint
 * @property {HTMLElement} flavours
 * @property {HTMLElement} purchase
 * @property {HTMLButtonElement} addButton
 * @property {HTMLElement} [stickyBar]
 * @property {HTMLButtonElement} [stickyAdd]
 * @property {HTMLElement} perLatte
 * @property {HTMLElement} [gifts]
 */

const SUPPLIES = [
  { id: '90', tins: 3, weekToken: '12', defaultSelected: false },
  { id: '60', tins: 2, weekToken: '8', defaultSelected: true },
  { id: '30', tins: 1, weekToken: '4', defaultSelected: false },
];

/**
 * @extends {Component<Refs>}
 */
class PdpOfferComponent extends Component {
  requiredRefs = ['config', 'supplies', 'flavours', 'purchase', 'addButton'];

  /** @type {Config | null} */
  #config = null;

  /** @type {string} */
  #supplyId = '60';

  /** @type {boolean} */
  #subscribe = true;

  /** @type {Map<string, number>} */
  #quantities = new Map();

  /** @type {boolean} */
  #pending = false;

  /** @type {boolean} */
  #galleryLock = false;

  /** @type {IntersectionObserver | undefined} */
  #buyObserver;

  /** @type {(() => void) | undefined} */
  #buyQueryCleanup;

  connectedCallback() {
    super.connectedCallback();
    this.#config = this.#readConfig();
    if (!this.#config || this.#config.flavours.length === 0) return;

    const available = SUPPLIES.filter((supply) => this.#supplyHasPlans(supply.weekToken));
    const preferred = available.find((supply) => supply.defaultSelected) || available[0];
    if (preferred) this.#supplyId = preferred.id;

    this.#quantities = this.#startingQuantities();
    this.#renderMediaThumbs();
    this.render();
    this.#watchBuyButton();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyObserver?.disconnect();
    this.#buyQueryCleanup?.();
  }

  /**
   * @param {MouseEvent} event
   */
  handleSupplySelect = (event) => {
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;
    const supplyId = button.dataset.supplyId;
    if (!supplyId) return;
    this.#supplyId = supplyId;
    this.#galleryLock = false;
    this.#quantities = this.#clampQuantities(this.#quantities, this.#tinCount());
    this.render();
  };

  /**
   * @param {MouseEvent} event
   */
  handleFlavourTap = (event) => {
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;
    const handle = button.dataset.handle;
    if (!handle) return;
    this.#galleryLock = false;
    this.#quantities = this.#tapFlavour(handle);
    this.render();
  };

  /**
   * @param {MouseEvent} event
   */
  handlePurchaseType = (event) => {
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;
    this.#subscribe = button.dataset.purchase === 'subscribe';
    this.render();
  };

  /**
   * @param {MouseEvent} event
   */
  showMedia = (event) => {
    const button = event.target instanceof Element ? event.target.closest('.pdp-offer__thumb') : null;
    if (!(button instanceof HTMLElement)) return;
    const src = button.dataset.src;
    const alt = button.dataset.alt || '';
    if (!src) return;
    this.#galleryLock = true;
    const { stageImage, mixPreview } = this.refs;
    this.#setMixStage(false);
    if (mixPreview) {
      mixPreview.hidden = true;
      mixPreview.replaceChildren();
    }
    if (stageImage) {
      stageImage.hidden = false;
      stageImage.src = src;
      stageImage.alt = alt;
    }
    this.#setActiveThumb(button);
  };

  /**
   * @param {MouseEvent} event
   */
  handleAddToCart = async (event) => {
    event.preventDefault();
    await this.#addToCart(false);
  };

  render() {
    this.#renderSupplies();
    this.#renderFlavours();
    this.#renderPurchase();
    this.#updateStage();
    this.#updateAddButton();
  }

  /**
   * @returns {Config | null}
   */
  #readConfig() {
    const { config } = this.refs;
    if (!config) return null;

    try {
      return JSON.parse(config.textContent || '{}');
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  #tinCount() {
    return this.#currentSupply()?.tins || 1;
  }

  #weekToken() {
    return this.#currentSupply()?.weekToken || '8';
  }

  #currentSupply() {
    return SUPPLIES.find((supply) => supply.id === this.#supplyId) || SUPPLIES[1];
  }

  /**
   * @param {string} weekToken
   */
  #supplyHasPlans(weekToken) {
    const flavours = this.#config?.flavours || [];
    return flavours.some((flavour) => flavour.plans && flavour.plans[weekToken]);
  }

  /**
   * @param {Flavour[]} flavours
   */
  #defaultFlavour(flavours) {
    return (
      flavours.find((flavour) => flavour.handle === 'vanilla-matcha-latte') ||
      flavours.find((flavour) => flavour.handle === this.#config?.currentHandle) ||
      flavours.find((flavour) => flavour.plans && Object.keys(flavour.plans).length > 0) ||
      flavours[0]
    );
  }

  /**
   * Vanilla is the best seller, so it is the starting flavour even when the page product is another tin.
   * @returns {Map<string, number>}
   */
  #startingQuantities() {
    const quantities = new Map();
    const flavours = this.#config?.flavours || [];
    const tinCount = this.#tinCount();
    const landing = this.#defaultFlavour(flavours);

    for (const flavour of flavours) {
      quantities.set(flavour.handle, flavour.handle === landing?.handle ? tinCount : 0);
    }

    return this.#clampQuantities(quantities, tinCount);
  }

  /**
   * @param {Map<string, number>} quantities
   * @param {number} tinCount
   */
  #clampQuantities(quantities, tinCount) {
    const next = new Map(quantities);
    const flavours = this.#config?.flavours || [];
    let total = this.#sum(next);

    if (total > tinCount) {
      for (let index = flavours.length - 1; index >= 0 && total > tinCount; index -= 1) {
        const handle = flavours[index].handle;
        const qty = next.get(handle) || 0;
        const reduce = Math.min(qty, total - tinCount);
        next.set(handle, qty - reduce);
        total -= reduce;
      }
    }

    if (total < tinCount) {
      const prefer = this.#defaultFlavour(flavours)?.handle;
      if (prefer) {
        next.set(prefer, (next.get(prefer) || 0) + (tinCount - total));
      }
    }

    return next;
  }

  /**
   * @param {string} handle
   */
  #tapFlavour(handle) {
    const next = new Map(this.#quantities);
    const tinCount = this.#tinCount();
    const flavours = this.#config?.flavours || [];
    const current = next.get(handle) || 0;
    const total = this.#sum(next);

    if (total < tinCount) {
      next.set(handle, current + 1);
      return next;
    }

    const donor = [...flavours].reverse().find((flavour) => {
      return flavour.handle !== handle && (next.get(flavour.handle) || 0) > 0;
    });

    if (!donor) return next;

    next.set(donor.handle, (next.get(donor.handle) || 0) - 1);
    next.set(handle, current + 1);
    return next;
  }

  /**
   * @param {Map<string, number>} quantities
   */
  #sum(quantities) {
    let total = 0;
    for (const qty of quantities.values()) {
      total += qty;
    }
    return total;
  }

  #totals() {
    const weekToken = this.#weekToken();
    const flavours = this.#config?.flavours || [];
    let subscribeCents = 0;
    let oneTimeCents = 0;
    let servings = 0;

    for (const flavour of flavours) {
      const qty = this.#quantities.get(flavour.handle) || 0;
      if (qty < 1) continue;
      const plan = flavour.plans?.[weekToken];
      oneTimeCents += flavour.oneTimeCents * qty;
      subscribeCents += (plan?.priceCents || flavour.oneTimeCents) * qty;
      servings += (flavour.servings || this.#config?.defaultServings || 60) * qty;
    }

    return { subscribeCents, oneTimeCents, servings };
  }

  #renderSupplies() {
    const { supplies } = this.refs;
    if (!supplies || !this.#config) return;

    supplies.setAttribute('role', 'radiogroup');
    supplies.replaceChildren();

    for (const supply of SUPPLIES) {
      if (!this.#supplyHasPlans(supply.weekToken)) continue;

      const selected = supply.id === this.#supplyId;
      const preview = this.#previewSupply(supply);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `pdp-offer__supply${selected ? ' pdp-offer__supply--selected' : ''}`;
      button.dataset.supplyId = supply.id;
      button.setAttribute('role', 'radio');
      button.setAttribute('aria-checked', selected ? 'true' : 'false');
      button.setAttribute('on:click', '/handleSupplySelect');

      const badges = [];
      if (supply.id === '90') {
        badges.push(this.#badge(this.#translate('most_popular')));
        badges.push(this.#badge(this.#translate('money_back_badge')));
      }

      const saveLabel = this.#translate('save_percent', { percent: this.#labeledSave(supply.id) });

      button.innerHTML = `
        ${badges.length ? `<span class="pdp-offer__supply-badges">${badges.join('')}</span>` : ''}
        <span class="pdp-offer__marker" aria-hidden="true"></span>
        <span class="pdp-offer__supply-copy">
          <span class="pdp-offer__supply-title-row">
            <span class="pdp-offer__supply-title">${this.#escape(this.#translate(`supply_${supply.id}_title`))}</span>
            <span class="pdp-offer__save">${this.#escape(saveLabel)}</span>
          </span>
          <span class="pdp-offer__supply-meta">${this.#escape(this.#translate(`supply_${supply.id}_meta`, {
            tins: supply.tins,
            per: this.#formatMoney(this.#perLatteCents(preview.subscribeCents, preview.servings), true),
          }))}</span>
        </span>
        <span class="pdp-offer__supply-prices">
          ${preview.oneTimeCents > preview.subscribeCents ? `<s>${this.#escape(this.#formatMoney(preview.oneTimeCents))}</s>` : ''}
          <strong>${this.#escape(this.#formatMoney(preview.subscribeCents))}</strong>
        </span>
      `;

      supplies.append(button);
    }
  }

  /**
   * @param {{ tins: number, weekToken: string }} supply
   */
  #previewSupply(supply) {
    const flavours = this.#config?.flavours || [];
    const currentHandle = this.#config?.currentHandle;
    const flavour =
      flavours.find((item) => item.handle === currentHandle && item.plans?.[supply.weekToken]) ||
      flavours.find((item) => item.plans?.[supply.weekToken]) ||
      flavours[0];
    const plan = flavour?.plans?.[supply.weekToken];
    const oneTimeCents = (flavour?.oneTimeCents || 0) * supply.tins;
    const subscribeCents = (plan?.priceCents || flavour?.oneTimeCents || 0) * supply.tins;
    const servings = (flavour?.servings || 60) * supply.tins;
    return { oneTimeCents, subscribeCents, servings };
  }

  #renderFlavours() {
    const { flavours, mixHint } = this.refs;
    if (!flavours || !this.#config) return;

    const tinCount = this.#tinCount();
    if (mixHint) {
      mixHint.textContent = this.#translate('mix_any', { count: tinCount });
    }

    flavours.replaceChildren();

    for (const flavour of this.#config.flavours) {
      const qty = this.#quantities.get(flavour.handle) || 0;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `pdp-offer__flavour${qty > 0 ? ' pdp-offer__flavour--selected' : ''}`;
      button.dataset.handle = flavour.handle;
      button.setAttribute('aria-pressed', qty > 0 ? 'true' : 'false');
      button.setAttribute('on:click', '/handleFlavourTap');
      button.innerHTML = `
        <span class="pdp-offer__flavour-emoji" aria-hidden="true">${this.#escape(flavour.emoji || '')}</span>
        <span class="pdp-offer__flavour-label">${this.#escape(flavour.label)}</span>
        ${qty > 0 ? `<span class="pdp-offer__flavour-qty">${qty}</span>` : ''}
      `;
      flavours.append(button);
    }
  }

  #renderPurchase() {
    const { purchase } = this.refs;
    if (!purchase) return;

    const supply = this.#currentSupply();
    const weeks = supply?.weekToken || '8';

    purchase.replaceChildren();

    const subscribe = document.createElement('button');
    subscribe.type = 'button';
    subscribe.className = `pdp-offer__choice${this.#subscribe ? ' pdp-offer__choice--selected' : ''}`;
    subscribe.dataset.purchase = 'subscribe';
    subscribe.setAttribute('aria-pressed', this.#subscribe ? 'true' : 'false');
    subscribe.setAttribute('on:click', '/handlePurchaseType');
    subscribe.innerHTML = `
      <span class="pdp-offer__choice-title">${this.#escape(this.#translate('subscribe_title'))}</span>
      <span class="pdp-offer__choice-badge">${this.#escape(this.#translate('recommended'))}</span>
      <span class="pdp-offer__choice-copy">${this.#escape(this.#translate('subscribe_copy', { weeks }))}</span>
    `;

    const onetime = document.createElement('button');
    onetime.type = 'button';
    onetime.className = `pdp-offer__choice${this.#subscribe ? '' : ' pdp-offer__choice--selected'}`;
    onetime.dataset.purchase = 'onetime';
    onetime.setAttribute('aria-pressed', this.#subscribe ? 'false' : 'true');
    onetime.setAttribute('on:click', '/handlePurchaseType');
    onetime.innerHTML = `
      <span class="pdp-offer__choice-title">${this.#escape(this.#translate('onetime_title'))}</span>
      <span class="pdp-offer__choice-copy">${this.#escape(this.#translate('onetime_copy'))}</span>
    `;

    purchase.append(subscribe, onetime);

    const { gifts } = this.refs;
    if (gifts) gifts.hidden = !this.#subscribe;
  }

  #renderMediaThumbs() {
    const { thumbs, stageImage } = this.refs;
    if (!thumbs || !this.#config) return;

    thumbs.replaceChildren();

    this.#config.media.forEach((media, index) => {
      const button = document.createElement('button');
      const selected = index === 0;
      button.type = 'button';
      button.className = selected ? 'pdp-offer__thumb pdp-offer__thumb--active' : 'pdp-offer__thumb';
      button.dataset.src = media.src;
      button.dataset.alt = media.alt;
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-selected', selected ? 'true' : 'false');
      button.setAttribute('on:click', '/showMedia');
      const image = document.createElement('img');
      image.className = 'pdp-offer__thumb-image';
      image.src = media.thumb;
      image.alt = media.alt;
      image.width = 96;
      image.height = 96;
      button.append(image);
      thumbs.append(button);

      if (selected && stageImage) {
        stageImage.hidden = false;
        stageImage.src = media.src;
        stageImage.alt = media.alt;
      }
    });
  }

  /**
   * @param {HTMLElement} active
   */
  #setActiveThumb(active) {
    const thumbs = active.parentElement?.querySelectorAll('.pdp-offer__thumb');
    if (!thumbs) return;
    for (const thumb of thumbs) {
      const selected = thumb === active;
      thumb.classList.toggle('pdp-offer__thumb--active', selected);
      thumb.setAttribute('aria-selected', selected ? 'true' : 'false');
    }
  }

  #updateAddButton() {
    const { addButton, perLatte } = this.refs;
    const totals = this.#totals();
    const chargeCents = this.#subscribe ? totals.subscribeCents : totals.oneTimeCents;
    const cafeCents = (this.#config?.cafeServeCents || 700) * totals.servings;
    const cafeSave = Math.max(cafeCents - chargeCents, 0);
    const per = this.#perLatteCents(chargeCents, totals.servings);

    if (addButton) {
      const compareHtml =
        this.#subscribe && totals.oneTimeCents > chargeCents
          ? `<s>${this.#escape(this.#formatMoney(totals.oneTimeCents))}</s>`
          : '';
      const label = `${this.#escape(this.#translate('add_to_cart', { price: this.#formatMoney(chargeCents) }))} ${compareHtml}`;
      const unavailable = chargeCents < 1 || this.#sum(this.#quantities) !== this.#tinCount();
      addButton.innerHTML = label;
      addButton.disabled = unavailable;
      if (this.refs.stickyAdd) {
        this.refs.stickyAdd.innerHTML = label;
        this.refs.stickyAdd.disabled = unavailable;
      }
    }

    if (perLatte) {
      perLatte.textContent = this.#translate('per_latte_line', {
        per: this.#formatMoney(per, true),
        save: this.#formatMoney(cafeSave),
        count: totals.servings,
      });
    }
  }

  /**
   * @param {boolean} active
   */
  #setMixStage(active) {
    const stage = this.refs.stageImage?.closest('.pdp-offer__stage');
    if (stage instanceof HTMLElement) stage.classList.toggle('pdp-offer__stage--mix', active);
  }

  #updateStage() {
    const { stageImage, mixPreview } = this.refs;
    if (!stageImage || !mixPreview || !this.#config || this.#galleryLock) return;

    const units = [];
    for (const flavour of this.#config.flavours) {
      const qty = this.#quantities.get(flavour.handle) || 0;
      for (let index = 0; index < qty; index += 1) {
        if (flavour.image) units.push(flavour);
      }
    }

    if (units.length < 2) {
      this.#setMixStage(false);
      mixPreview.hidden = true;
      mixPreview.replaceChildren();
      const only = units[0];
      if (only?.image) {
        stageImage.hidden = false;
        stageImage.src = only.image;
        stageImage.alt = only.label;
      }
      return;
    }

    this.#setMixStage(true);
    stageImage.hidden = true;
    mixPreview.hidden = false;
    mixPreview.className = `pdp-offer__mix-preview pdp-offer__mix-preview--${Math.min(units.length, 3)}`;
    mixPreview.replaceChildren();

    for (const flavour of units) {
      const image = document.createElement('img');
      image.className = 'pdp-offer__mix-image';
      image.src = flavour.image;
      image.alt = flavour.label;
      image.width = 600;
      image.height = 600;
      mixPreview.append(image);
    }
  }

  /**
   * @param {string} supplyId
   */
  #labeledSave(supplyId) {
    if (supplyId === '90') return 30;
    if (supplyId === '60') return 20;
    return 15;
  }

  #watchBuyButton() {
    const button = this.refs.addButton;
    const bar = this.refs.stickyBar;
    if (!button || !bar || typeof IntersectionObserver === 'undefined') return;

    const query = window.matchMedia('(max-width: 749px)');
    const sync = (entry) => {
      if (!query.matches || !entry) {
        bar.hidden = true;
        return;
      }
      const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.hidden = !scrolledPast;
    };

    this.#buyObserver = new IntersectionObserver((entries) => {
      sync(entries[0]);
    });
    this.#buyObserver.observe(button);

    const onChange = () => {
      if (!query.matches) bar.hidden = true;
    };
    query.addEventListener('change', onChange);
    this.#buyQueryCleanup = () => query.removeEventListener('change', onChange);
  }

  /**
   * @param {boolean} checkout
   */
  async #addToCart(checkout) {
    const { addButton, stickyAdd } = this.refs;
    if (!addButton || this.#pending) return;

    const items = this.#cartItems();
    if (items.length === 0) return;

    this.#pending = true;
    addButton.disabled = true;
    if (stickyAdd) stickyAdd.disabled = true;

    const sectionIds = [];
    for (const el of document.querySelectorAll('cart-items-component')) {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sectionIds.push(el.dataset.sectionId);
      }
    }

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items,
          sections: sectionIds.join(','),
        }),
      });
      const data = await response.json();

      if (data.status) {
        this.dispatchEvent(
          new CartErrorEvent(this.id || 'pdp-offer', data.message, data.description, data.errors)
        );
        return;
      }

      this.dispatchEvent(
        new CartAddEvent({}, this.id || 'pdp-offer', {
          source: 'pdp-offer',
          itemCount: items.length,
          sections: data.sections,
        })
      );

      if (checkout) {
        window.location.assign('/checkout');
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.#pending = false;
      addButton.disabled = false;
      if (stickyAdd) stickyAdd.disabled = false;
      this.#updateAddButton();
    }
  }

  #cartItems() {
    const weekToken = this.#weekToken();
    const flavours = this.#config?.flavours || [];
    const items = [];

    for (const flavour of flavours) {
      const qty = this.#quantities.get(flavour.handle) || 0;
      if (qty < 1 || !flavour.variantId) continue;
      const item = {
        id: Number(flavour.variantId),
        quantity: qty,
      };
      if (this.#subscribe) {
        const plan = flavour.plans?.[weekToken];
        if (plan?.id) item.selling_plan = Number(plan.id);
      }
      items.push(item);
    }

    return items;
  }

  /**
   * @param {number} cents
   * @param {number} servings
   */
  #perLatteCents(cents, servings) {
    if (servings < 1 || cents < 1) return 0;
    return Math.round(cents / servings);
  }

  /**
   * @param {string} key
   * @param {Record<string, string | number>} [replacements]
   */
  #translate(key, replacements = {}) {
    const strings = {
      choose_supply: 'Choose your supply',
      supply_90_title: '90-Day Supply',
      supply_60_title: '60-Day Supply',
      supply_30_title: '30-Day Supply',
      supply_90_meta: '3 tins · {{per}} per latte',
      supply_60_meta: '2 tins · {{per}} per latte',
      supply_30_meta: '1 tin · {{per}} per latte',
      save_percent: 'SAVE {{percent}}%',
      most_popular: 'Most popular',
      money_back_badge: '90-day money back',
      mix_any: 'Mix any {{count}}',
      subscribe_title: 'Subscribe & save',
      subscribe_copy: 'Delivered every {{weeks}} weeks. A free gift with every order. Pause, skip or cancel anytime.',
      recommended: 'Recommended',
      onetime_title: 'One-time purchase',
      onetime_copy: 'Full price. No discount and no gifts.',
      add_to_cart: 'Add to cart · {{price}}',
      per_latte_line: '{{per}} per latte · save {{save}} vs {{count}} café lattes',
    };

    let text = strings[key] || key;
    for (const [token, value] of Object.entries(replacements)) {
      text = text.replaceAll(`{{${token}}}`, String(value));
    }
    return text;
  }

  /**
   * @param {string} text
   */
  #badge(text) {
    return `<span class="pdp-offer__badge">${this.#escape(text)}</span>`;
  }

  /**
   * @param {boolean} [fractional]
   */
  #moneyFormat(fractional = false) {
    let format = String(this.#config?.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');

    if (fractional) {
      format = format
        .replaceAll('amount_no_decimals_with_comma_separator', 'amount_with_comma_separator')
        .replaceAll('amount_no_decimals_with_space_separator', 'amount_with_space_separator')
        .replaceAll('amount_no_decimals', 'amount');
    }

    return format;
  }

  /**
   * @param {number} cents
   * @param {boolean} [fractional]
   */
  #formatMoney(cents, fractional = false) {
    return formatMoney(Math.max(cents, 0), this.#moneyFormat(fractional), this.#config?.currency || 'USD');
  }

  /**
   * @param {string} value
   */
  #escape(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;');
  }
}

if (!customElements.get('pdp-offer-component')) {
  customElements.define('pdp-offer-component', PdpOfferComponent);
}
